import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Edit3, Trash2, X, Save, Eye } from "lucide-react";

const empty = () => ({ id: null, title: "", excerpt: "", body: "", cover_image: "", category: "Hosting", published: true });

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [draft, setDraft] = useState(null);

  const load = () => api.get("/admin/blog/posts").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/admin/blog/posts", draft);
      toast.success("Post saved.");
      setDraft(null);
      load();
    } catch { toast.error("Save failed."); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/posts/${id}`);
    toast.success("Deleted.");
    load();
  };

  return (
    <div className="text-white">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Blog</div>
          <h1 className="font-display text-4xl">Write, edit, publish.</h1>
        </div>
        <Button onClick={() => setDraft(empty())} data-testid="admin-new-blog" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white">
          <Plus className="w-4 h-4 mr-2" /> New post
        </Button>
      </div>

      <div className="rounded-3xl bg-white/5 border border-white/10 divide-y divide-white/5">
        {posts.map((p) => (
          <div key={p.id} data-testid={`admin-blog-${p.slug}`} className="p-5 flex items-center gap-4">
            {p.cover_image && <img src={p.cover_image} className="w-16 h-16 rounded-xl object-cover" alt="" />}
            <div className="flex-1">
              <div className="font-display text-lg">{p.title}</div>
              <div className="text-sm text-white/60">{p.category} · {p.published ? "Published" : "Draft"} · /{p.slug}</div>
            </div>
            <Button asChild variant="ghost" className="text-white/80"><a target="_blank" rel="noreferrer" href={`/blog/${p.slug}`}><Eye className="w-4 h-4" /></a></Button>
            <Button variant="ghost" onClick={() => setDraft(p)} data-testid={`admin-blog-edit-${p.slug}`} className="text-white hover:bg-white/10 rounded-full"><Edit3 className="w-4 h-4" /></Button>
            <Button variant="ghost" onClick={() => del(p.id)} data-testid={`admin-blog-del-${p.slug}`} className="text-brand-pink hover:bg-brand-pink/10 rounded-full"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        {posts.length === 0 && <div className="p-10 text-white/50 text-center">No posts yet.</div>}
      </div>

      {draft && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur overflow-y-auto py-10 px-4">
          <div className="max-w-3xl mx-auto rounded-3xl bg-neutral-900 border border-white/10 p-8" data-testid="admin-blog-editor">
            <div className="flex justify-between mb-6">
              <div className="font-display text-2xl">{draft.id ? "Edit post" : "New post"}</div>
              <button onClick={() => setDraft(null)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="mb-2 block text-white/80 text-xs uppercase tracking-widest">Title</Label>
                <Input data-testid="admin-blog-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="rounded-full h-11 bg-white/5 border-white/10 text-white" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block text-white/80 text-xs uppercase tracking-widest">Category</Label>
                  <Input data-testid="admin-blog-cat" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="rounded-full h-11 bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="mb-2 block text-white/80 text-xs uppercase tracking-widest">Cover image URL</Label>
                  <Input data-testid="admin-blog-cover" value={draft.cover_image} onChange={(e) => setDraft({ ...draft, cover_image: e.target.value })} className="rounded-full h-11 bg-white/5 border-white/10 text-white" />
                </div>
              </div>
              <div>
                <Label className="mb-2 block text-white/80 text-xs uppercase tracking-widest">Excerpt</Label>
                <Textarea data-testid="admin-blog-excerpt" rows={2} value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} className="rounded-2xl bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="mb-2 block text-white/80 text-xs uppercase tracking-widest">Body (supports ## H2, - lists, &gt; quote, **bold**)</Label>
                <Textarea data-testid="admin-blog-body" rows={14} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} className="rounded-2xl bg-white/5 border-white/10 text-white" />
              </div>
              <label className="flex items-center gap-2 text-sm text-white/80">
                <input type="checkbox" checked={draft.published} onChange={(e) => setDraft({ ...draft, published: e.target.checked })} /> Published
              </label>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <Button variant="ghost" onClick={() => setDraft(null)} className="text-white/70">Cancel</Button>
              <Button onClick={save} data-testid="admin-save-blog" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white"><Save className="w-4 h-4 mr-2" /> Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
